import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

const SHIELD = "M72 6 L132 30 V74 C132 106 106 130 72 142 C38 130 12 106 12 74 V30 Z";

function Gold({ id }: { id: string }) {
  return <defs><linearGradient id={id} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#F3C66B" /><stop offset="1" stopColor="#D99A2B" /></linearGradient></defs>;
}

function CheckV() {
  const id = `vvg${React.useId().replace(/:/g, "")}`;
  return (
    <svg viewBox="0 0 72 68" className="ml-[0.04em] inline-block h-[1.32em] w-[1.36em] -translate-y-[0.14em] transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110" aria-hidden="true">
      <Gold id={id} />
      <path d="M8 34 L30 60 L64 8" fill="none" stroke={`url(#${id})`} strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IRMark({ ink, big }: { ink: string; big?: boolean }) {
  const id = `irg${React.useId().replace(/:/g, "")}`;
  return (
    <svg viewBox="0 0 144 150" className={big ? "h-3.5 w-3.5 sm:h-[18px] sm:w-[18px]" : "h-3.5 w-3.5"} aria-hidden="true">
      <Gold id={id} />
      <path d={SHIELD} fill="none" stroke={ink} strokeWidth="12" strokeLinejoin="round" />
      <circle cx="80" cy="66" r="28" fill="none" stroke={`url(#${id})`} strokeWidth="15" />
      <path d="M100 88 L124 114" stroke={`url(#${id})`} strokeWidth="17" strokeLinecap="round" />
    </svg>
  );
}

export function BrandMark({ light = false, big = false }: { light?: boolean; big?: boolean }) {
  const ink = light ? "#FFFFFF" : "#1B365D";
  const gold = light ? "text-[#E8B04A]" : "text-[#C98A1E]";
  return (
    <Link to="/" data-testid="public-nav-brand-logo" aria-label="Info Retrieval Services" className="group inline-flex w-fit flex-col items-start leading-none">
      <span className={`flex items-center gap-2 font-heading ${big ? "text-[22px] sm:text-[34px]" : "text-[24px]"} font-extrabold tracking-[-0.035em] ${light ? "text-white" : "text-[#1B365D]"}`}>
        <span className={big ? "[&>svg]:h-7 [&>svg]:w-7 sm:[&>svg]:h-10 sm:[&>svg]:w-10" : "[&>svg]:h-7 [&>svg]:w-7"}><IRMark ink={ink} /></span>
        <span>Info<span className={gold}>Retrieval</span></span>
      </span>
      <span className={`mt-1.5 flex w-full items-center justify-center border-t pt-1.5 ${big ? "text-[8px] sm:text-[11px]" : "text-[8.5px]"} font-semibold uppercase tracking-[0.3em] ${light ? "border-white/20 text-white/70" : "border-[#1B365D]/15 text-[#64748B]"}`} data-testid="brand-division-line">
        Services
      </span>
    </Link>
  );
}

const NAV = {
  en: { status: "Check status", order: "Order codes", applyShort: "Apply", apply: "Employee/Volunteer application", footStatus: "Check application status", staff: "Staff sign in", secure: "Encrypted connection \u00b7 Your information is protected" },
  es: { status: "Consultar estado", order: "Comprar c\u00f3digos", applyShort: "Solicitar", apply: "Solicitud de empleado/voluntario", footStatus: "Consultar el estado de mi solicitud", staff: "Acceso del personal", secure: "Conexi\u00f3n cifrada \u00b7 Su informaci\u00f3n est\u00e1 protegida" },
};

const readLang = () => (localStorage.getItem("irs_lang") === "es" ? "es" : "en");

export const setSiteLang = (l: "en" | "es") => {
  localStorage.setItem("irs_lang", l);
  window.dispatchEvent(new Event("irs-lang"));
};

export function useSiteLang() {
  const [lang, setLang] = React.useState(readLang);
  React.useEffect(() => {
    const on = () => { setLang(readLang()); };
    window.addEventListener("irs-lang", on);
    return () => { window.removeEventListener("irs-lang", on); };
  }, []);
  return lang;
}

export default function PublicShell({ children }: { children: React.ReactNode }) {
  const lang = useSiteLang();
  const n = NAV[lang];
  const q = lang === "es" ? "?lang=es" : "";
  return (
    <div className="min-h-screen bg-[#F9F8F5] text-[#1A2536] grain">
      <header className="sticky top-0 z-50 border-b border-[#E2E8F0]/80 bg-[#FAF9F5]/90 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-2 px-4 sm:h-28 sm:px-6 lg:px-8">
          <BrandMark big />
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link data-testid="nav-status-link" to={`/check-status${q}`} className="hidden rounded-xl px-3 py-2 text-sm font-semibold text-[#334155] transition-colors hover:bg-[#F1EFEA] hover:text-[#1B365D] sm:inline-flex">
              {n.status}
            </Link>
            <Link data-testid="nav-order-link" to="/order" className="whitespace-nowrap rounded-xl px-2 py-2 text-xs font-semibold text-[#334155] transition-colors hover:bg-[#F1EFEA] hover:text-[#1B365D] sm:px-3 sm:text-sm">
              {n.order}
            </Link>
            <Link data-testid="nav-apply-link" to="/apply" className="whitespace-nowrap rounded-xl bg-[#1B365D] px-3 py-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#142847] sm:px-4 sm:text-sm">
              <span className="sm:hidden">{n.applyShort}</span><span className="hidden sm:inline">{n.apply}</span>
            </Link>
          </nav>
        </div>
      </header>
      <main>{children}</main>
      <footer className="mt-24 border-t border-[#E2E8F0] bg-[#F1EFEA]/60">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-10 text-sm text-[#64748B] sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <p>© {new Date().getFullYear()} Info Retrieval Services. Secure background screening.</p>
          <p className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#0D9488]" /> {n.secure}
            <Link to={`/check-status${q}`} data-testid="footer-status-link" className="ml-3 font-semibold text-[#64748B] hover:text-[#1B365D]">{n.footStatus}</Link>
            <Link to="/admin/login" data-testid="footer-staff-login-link" className="ml-3 font-semibold text-[#64748B] hover:text-[#1B365D]">{n.staff}</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
